import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import type { Client } from '@/clients/types';
import type { FeatureOverlayState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { fetchFittingsForViewport } from '@/api/fittingsApi';
import { attachViewportLoader, EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

export const FITTINGS_SOURCE_ID = 'orbit-fittings-source';

// The Fittings semantic band starts at zoom 17.5, but the viewport loader floors the map zoom before
// its min-zoom check (see attachViewportLoader), so a fractional 17.5 would never fetch until zoom 18.
// Use the floored value 17 here so fittings load across the whole 17.5+ band; semantic-zoom
// visibility still gates the actual display to the Fittings level.
const FITTINGS_MIN_ZOOM = 17;

// Every fitting renders as a uniform white disc (r12) with a blue (#007CFF) centre dot
// and a soft drop shadow. Drawn as crisp vector circles — the disc is one circle (r6 blue
// fill + 7px white stroke → r12 outer), the shadow a blurred dark circle — so the hover
// grow can scale them smoothly. Per-fittingType colour coding was dropped (the type is
// reachable via the hover tooltip).

function registerFittingsSourceAndLayer(mlMap: maplibregl.Map, data: FeatureCollection, visible: boolean): void {
    const visibility = visible ? 'visible' : 'none';

    try {
        if (mlMap.getSource(FITTINGS_SOURCE_ID)) {
            (mlMap.getSource(FITTINGS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            // promoteId exposes each fitting's `id` as the feature id so the hover-grow tween
            // can target it via setFeatureState (see useFittingInteractions).
            mlMap.addSource(FITTINGS_SOURCE_ID, { type: 'geojson', data, promoteId: 'id' });
        }
    } catch {
        return;
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.fittingsShadow)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.fittingsShadow,
                    type: 'circle',
                    source: FITTINGS_SOURCE_ID,
                    minzoom: FITTINGS_MIN_ZOOM,
                    layout: { visibility },
                    paint: {
                        // Soft offset drop shadow under the disc: blurred so it feathers out by
                        // r14, at 12% black, nudged 1.5px down (screen space). Grows with the disc on
                        // hover via the hoverProgress feature-state.
                        'circle-radius': [
                            '*',
                            14,
                            ['+', 1, ['*', 0.5, ['number', ['feature-state', 'hoverProgress'], 0]]],
                        ],
                        'circle-color': '#000000',
                        'circle-opacity': 0.12,
                        'circle-blur': 0.4,
                        'circle-translate': [0, 1.5],
                        'circle-translate-anchor': 'viewport',
                    },
                },
                'assetOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.fittings)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.fittings,
                    type: 'circle',
                    source: FITTINGS_SOURCE_ID,
                    minzoom: FITTINGS_MIN_ZOOM,
                    layout: { visibility },
                    paint: {
                        // White disc (7px stroke) with a blue centre dot (r5) → r12 outer. Both scale
                        // by up to 50% with the hoverProgress feature-state (0→1, tweened in
                        // useFittingInteractions) so the hovered fitting grows smoothly.
                        'circle-radius': [
                            '*',
                            5,
                            ['+', 1, ['*', 0.5, ['number', ['feature-state', 'hoverProgress'], 0]]],
                        ],
                        'circle-color': '#007CFF',
                        'circle-stroke-width': [
                            '*',
                            7,
                            ['+', 1, ['*', 0.5, ['number', ['feature-state', 'hoverProgress'], 0]]],
                        ],
                        'circle-stroke-color': '#ffffff',
                    },
                },
                'assetOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

/**
 * Loads water fitting GeoJSON for the selected client, registers the MapLibre GeoJSON
 * source and two circle layers (a drop shadow and the white-disc marker), and
 * re-registers them after every base map style change.
 * Visibility is set at registration time from `featureOverlays.fittings`; subsequent
 * toggle changes are handled by `useMapLayerVisibility` via the populated `fittings.mapLayerIds`.
 *
 * Safe when:
 *   - No client is selected (clears source data, skips fetch).
 *   - Fitting data has not loaded yet (source contains empty FeatureCollection).
 *   - Map layers do not exist (all operations guard with getLayer/getSource).
 *   - API requests fail (error is surfaced via showErrorToast; map continues working).
 */
export function useFittingsLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    selectedClient: Client | null,
    featureOverlays: FeatureOverlayState,
): void {
    const fittingsDataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);

    // Keep a ref so the registration effect can read the current overlay state
    // without re-running when featureOverlays changes (useMapLayerVisibility
    // owns subsequent visibility toggling via mapLayerIds).
    const featureOverlaysRef = useRef(featureOverlays);
    useLayoutEffect(() => {
        featureOverlaysRef.current = featureOverlays;
    });

    // Load fitting data for the current viewport when client/map-ready/enabled state changes.
    // Re-loads on map moveend (covers both pan and zoom end).
    useEffect(() => {
        fittingsDataRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, FITTINGS_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!selectedClient || !isMapLoaded) return;
        if (!featureOverlays.fittings) return;

        return attachViewportLoader(mapRef, {
            sourceId: FITTINGS_SOURCE_ID,
            minZoom: FITTINGS_MIN_ZOOM,
            clientId: selectedClient.id,
            dataRef: fittingsDataRef,
            fetchFn: fetchFittingsForViewport,
        });
    }, [selectedClient, isMapLoaded, featureOverlays.fittings, mapRef]);

    // Register fitting source and layer when the map is ready, and re-register on
    // every style.load (base map switches reload all sources and layers).
    // Re-registration reuses the already-loaded data from fittingsDataRef — no refetch.
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerFittingsSourceAndLayer(mlMap, fittingsDataRef.current, featureOverlaysRef.current.fittings);
        };

        register();
        mlMap.on('style.load', register);

        return () => {
            mlMap.off('style.load', register);
        };
    }, [isMapLoaded, mapRef]);
}
